import React, { useState, useRef, useEffect } from 'react';
import {
  Textarea,
  Box,
  VStack,
  HStack,
  Avatar,
  Text,
  TextareaProps,
} from '@chakra-ui/react';
import { useSearchUsers } from '@/api';
import type { UserSearchResult } from '@/types/social';

interface MentionInputProps extends Omit<TextareaProps, 'value' | 'onChange'> {
  value: string;
  onChange: (value: string) => void;
}

/**
 * Helper function to detect if user is typing a mention
 */
const detectMention = (text: string, cursorPosition: number): string | null => {
  const beforeCursor = text.slice(0, cursorPosition);
  const match = beforeCursor.match(/@(\w*)$/);
  return match ? match[1] : null;
};

/**
 * Helper function to format user name for mention
 */
const formatMention = (userName: string): string => {
  return `@${userName.replace(/ /g, '_')}`;
};

export function MentionInput({ value, onChange, ...textareaProps }: MentionInputProps) {
  const [cursorPosition, setCursorPosition] = useState(0);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Search for users when typing a mention
  const { data: searchResults } = useSearchUsers(
    {
      q: mentionQuery || '',
      limit: 5,
    },
    {
      enabled: mentionQuery !== null && mentionQuery.length >= 1,
    }
  );

  const mentionSuggestions = searchResults || [];

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    const newPosition = e.target.selectionStart || 0;

    onChange(newValue);
    setCursorPosition(newPosition);

    // Check if user is typing a mention
    const mention = detectMention(newValue, newPosition);
    setMentionQuery(mention);
  };

  const insertMention = (userName: string, userId: number) => {
    if (!textareaRef.current) return;

    const beforeCursor = value.slice(0, cursorPosition);
    const afterCursor = value.slice(cursorPosition);

    // Find the @ symbol position
    const atPosition = beforeCursor.lastIndexOf('@');
    const beforeMention = value.slice(0, atPosition);
    const mention = formatMention(userName);

    const newValue = `${beforeMention}${mention} ${afterCursor}`;
    onChange(newValue);
    setMentionQuery(null);

    // Set cursor after the mention
    const newCursorPos = beforeMention.length + mention.length + 1;
    setTimeout(() => {
      textareaRef.current?.setSelectionRange(newCursorPos, newCursorPos);
      textareaRef.current?.focus();
    }, 0);
  };

  return (
    <Box position="relative">
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={handleTextChange}
        {...textareaProps}
      />

      {/* Mention Suggestions Dropdown */}
      {mentionQuery !== null && mentionSuggestions.length > 0 && (
        <Box
          position="absolute"
          bottom="100%"
          left={0}
          right={0}
          bg="white"
          borderRadius="md"
          boxShadow="lg"
          borderWidth="1px"
          borderColor="gray.200"
          maxH="200px"
          overflowY="auto"
          zIndex={10}
          mb={2}
        >
          <VStack spacing={0} align="stretch">
            {mentionSuggestions.map((user: UserSearchResult) => (
              <HStack
                key={user.id}
                p={2}
                spacing={3}
                _hover={{ bg: 'gray.100' }}
                cursor="pointer"
                onClick={() => insertMention(user.name, user.id)}
              >
                <Avatar
                  size="sm"
                  name={user.name}
                  src={user.photoUrl || user.profilePicture || undefined}
                />
                <VStack align="start" spacing={0} flex={1}>
                  <Text fontSize="sm" fontWeight="medium">
                    {user.name}
                  </Text>
                  {user.location && (
                    <Text fontSize="xs" color="gray.600">
                      {user.location}
                    </Text>
                  )}
                </VStack>
              </HStack>
            ))}
          </VStack>
        </Box>
      )}
    </Box>
  );
}
